import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, Button, TextField, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tabs, Tab, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Menu, MenuItem,
  ListItemIcon, ListItemText
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getAllPosts, deletePost } from "api/canblog/CANblogAPI";
import { ROUTE_CANBLOG_EDITOR } from "constants/routerPath";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";

function BlogPostsList() {
  const navigate = useNavigate();
  const { notifyError } = useNotificationProvider();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // all, draft, published
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPost, setMenuPost] = useState(null);

  // Fetch posts on mount and when filter changes
  useEffect(() => {
    fetchPosts();
  }, [statusFilter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const status = statusFilter === "all" ? "" : statusFilter;
      const data = await getAllPosts(status, 100, 0);
      setPosts(data || []);
    } catch (err) {
      console.error("[BlogPostsList] Failed to fetch posts:", err);
      notifyError("Failed to load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Filter posts by search query
  const filteredPosts = posts.filter(post =>
    post.Title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (postId) => {
    navigate(`${ROUTE_CANBLOG_EDITOR}?id=${postId}`);
  };

  const handleNewPost = () => {
    navigate(ROUTE_CANBLOG_EDITOR);
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (postToDelete) {
      try {
        await deletePost(postToDelete.BlogPostId);
        fetchPosts(); // Refresh list
      } catch (err) {
        console.error("[BlogPostsList] Failed to delete post:", err);
        notifyError("Failed to delete post. Please try again.");
      }
    }
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  const handleView = (slug) => {
    // Open public blog URL in new tab
    window.open(`/blog/${slug}`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Render status chip with appropriate color
  const renderStatusChip = (post) => {
    if (post.Status === "published" && post.PublishDate && new Date(post.PublishDate) > new Date()) {
      return <Chip label="scheduled" size="small" color="warning" />;
    }
    return (
      <Chip
        label={post.Status}
        size="small"
        color={post.Status === "published" ? "success" : "default"}
      />
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Blog Posts</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewPost}>
          New Post
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Tabs value={statusFilter} onChange={(e, v) => setStatusFilter(v)}>
          <Tab label="All" value="all" />
          <Tab label="Draft" value="draft" />
          <Tab label="Published" value="published" />
        </Tabs>
        <TextField
          size="small"
          placeholder="Search by title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 250 }}
        />
      </Box>

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        /* Posts Table */
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Publish Date</TableCell>
                <TableCell>Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No posts found</TableCell>
                </TableRow>
              ) : (
                filteredPosts.map((post) => (
                  <TableRow
                    key={post.BlogPostId}
                    hover
                  >
                    <TableCell>{post.Title}</TableCell>
                    <TableCell>{renderStatusChip(post)}</TableCell>
                    <TableCell>{post.CategoryName || "-"}</TableCell>
                    <TableCell>{post.AuthorName || "-"}</TableCell>
                    <TableCell>{formatDate(post.PublishDate)}</TableCell>
                    <TableCell>{formatDate(post.ModifyDate)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => { setMenuAnchorEl(e.currentTarget); setMenuPost(post); }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => { handleEdit(menuPost?.BlogPostId); setMenuAnchorEl(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleView(menuPost?.Slug); setMenuAnchorEl(null); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleDeleteClick(menuPost); setMenuAnchorEl(null); }} sx={{ color: "error.main" }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{postToDelete?.Title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default BlogPostsList;
